import { fazerLogout } from '../utils/logout';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from './Logo';
import Desdobramento from './modals/Desdobramento';
import '../styles/Dashboard.css';
import '../styles/ConsultaPedido.css';
import { getAuthSession } from '../utils/authStorage';

// Cache com TTL (5 minutos)
const CACHE_TTL = 5 * 60 * 1000;

function salvarCacheConsulta(orderId, data) {
  try {
    const cache = JSON.parse(sessionStorage.getItem('consultaPedidoCache') || '{}');
    cache[orderId] = {
      data,
      timestamp: Date.now(),
    };
    sessionStorage.setItem('consultaPedidoCache', JSON.stringify(cache));
  } catch {
    // Ignora erros de storage
  }
}

function obterCacheConsulta(orderId) {
  try {
    const cache = JSON.parse(sessionStorage.getItem('consultaPedidoCache') || '{}');
    const entry = cache[orderId];
    if (!entry) return null;
    
    const idade = Date.now() - entry.timestamp;
    if (idade > CACHE_TTL) {
      delete cache[orderId];
      sessionStorage.setItem('consultaPedidoCache', JSON.stringify(cache));
      return null;
    }
    
    return entry.data;
  } catch {
    return null;
  }
}

const STATUS_MAP = {
  P: { texto: 'Pendente',   cor: '#f59e0b', bg: '#fffbeb' },
  M: { texto: 'Montado',    cor: '#8b5cf6', bg: '#f5f3ff' },
  B: { texto: 'Bloqueado',  cor: '#ef4444', bg: '#fef2f2' },
  C: { texto: 'Cancelado',  cor: '#6b7280', bg: '#f9fafb' },
  F: { texto: 'Faturado',   cor: '#10b981', bg: '#ecfdf5' },
  L: { texto: 'Liberado',   cor: '#3b82f6', bg: '#eff6ff' },
};

const SALE_ORIGIN_MAP = {
  B: 'Balcão',
  R: 'Balcão Reserva',
  K: 'Venda Broker',
  C: 'Callcenter',
  F: 'Força de Venda',
  T: 'Telemarketing',
  W: 'Venda WEB',
  A: 'Auto Serviço (Varejo)',
};

const SALE_TYPE_MAP = {
  1: 'Venda normal',
  5: 'Bonificação ou brinde',
  7: 'Venda para Entrega Futura',
  8: 'Simples Remessa de Venda para Entrega Futura',
};

const PAYMENT_PLAN_MAP = {
  1: 'À Vista',
  10: '30/60 Dias',
  13: '30/60/90 Dias',
  30: '30 Dias',
  64: 'Plano Negocial',
  70: 'Débito',
  71: 'Crédito à Vista',
  101: '3X',
  106: '8X',
  111: 'PFUN - À Vista',
  112: 'PFUN - 2X',
  113: 'PFUN - 3X',
};

function getStatus(code) {
  return STATUS_MAP[code] || { texto: code || '—', cor: '#6b7280', bg: '#f9fafb' };
}

function getPaymentPlan(code) {
  return PAYMENT_PLAN_MAP[code] || code || '—';
}

function formatarMoeda(v) {
  return Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatarData(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('pt-BR'); } catch { return d; }
}

function formatarDoc(doc) {
  if (!doc) return '—';
  const s = String(doc).replace(/\D/g, '');
  if (s.length === 14) return s.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  if (s.length === 11) return s.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  return doc;
}

export default function ConsultaPedido() {
  const [numeroPedido, setNumeroPedido] = useState('');
  const [pedido, setPedido] = useState(null);
  const [pedidoDesdobramento, setPedidoDesdobramento] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const navigate = useNavigate();

  const { customerName } = getAuthSession();
  const clienteNome = customerName || '';

  const handleBuscar = async (e) => {
    e.preventDefault();
    if (!numeroPedido.trim()) return;

    const pedidoId = numeroPedido.trim();
    
    // Tenta carregar do cache primeiro
    const cachedPedido = obterCacheConsulta(pedidoId);
    if (cachedPedido) {
      setPedido(cachedPedido);
      setErro('');
      return;
    }

    setErro('');
    setPedido(null);
    setCarregando(true);

    try {
      const { token } = getAuthSession();
      const response = await fetch('/api/orders/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token,
        },
        body: JSON.stringify({
          orderId: pedidoId,
        }),
      });

      const data = await response.json();

      if (data.ok && data.pedido) {
        salvarCacheConsulta(pedidoId, data.pedido);
        setPedido(data.pedido);
      } else {
        setErro(data.erro || 'Pedido não encontrado.');
      }
    } catch (err) {
      setErro('Erro ao conectar com o servidor. Verifique se o backend está rodando.');
    } finally {
      setCarregando(false);
    }
  };

  const handleLogout = () => fazerLogout(navigate);

  const status = pedido ? getStatus(pedido.orderStatus) : null;

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="header-left">
          <Logo size="small" onClick={() => navigate('/dashboard')} />
          <div className="header-text">
            <h1>Consulta de Pedido</h1>
            <p className="cliente-info">{clienteNome}</p>
          </div>
        </div>
        <nav className="nav-buttons">
          <button onClick={() => navigate('/pedidos')} className="btn-nav">Pedidos</button>
          <button onClick={() => navigate('/dashboard')} className="btn-nav">Dashboard</button>
          <button onClick={handleLogout} className="btn-logout">
            <span className="btn-logout__icon" aria-hidden>
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M16 17l5-5-5-5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M21 12H9" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9 19H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            Sair
          </button>
        </nav>
      </header>

      <div className="dashboard-content">

        {/* Formulário de busca */}
        <div className="consulta-search-box">
          <h2 className="consulta-titulo">🔍 Consultar Pedido por Número</h2>
          <p className="consulta-subtitulo">
            Informe o número do pedido para ver detalhes, status e Nota Fiscal.
            A consulta é restrita ao seu CNPJ.
          </p>

          <form className="consulta-form" onSubmit={handleBuscar}>
            <input
              type="number"
              className="consulta-input"
              placeholder="Ex: 123456"
              value={numeroPedido}
              onChange={(e) => setNumeroPedido(e.target.value)}
              min="1"
              disabled={carregando}
              required
            />
            <button type="submit" className="consulta-btn" disabled={carregando || !numeroPedido.trim()}>
              {carregando ? (
                <span className="consulta-spinner">⟳ Buscando...</span>
              ) : (
                '🔍 Buscar'
              )}
            </button>
          </form>

          <div className="consulta-divider">
            <span>ou</span>
          </div>

          <button 
            className="consulta-btn-secondary" 
            onClick={() => navigate('/pedidos')}
          >
            📋 Ver Todos os Meus Pedidos
          </button>

          {erro && (
            <div className="consulta-erro">
              <span>⚠️</span> {erro}
            </div>
          )}
        </div>

        {/* Resultado */}
        {pedido && (
          <div className="consulta-resultado">

            {/* Cabeçalho do pedido */}
            <div className="resultado-header">
              <div className="resultado-id">
                <span className="resultado-label">Pedido</span>
                <span className="resultado-numero">#{pedido.idPedido}</span>
              </div>
              <span
                className="resultado-status"
                style={{ color: status.cor, backgroundColor: status.bg }}
              >
                {status.texto}
              </span>
            </div>

            {(pedido.saleType === '7' || pedido.saleType === 7) && (
              <div style={{ marginBottom: '16px' }}>
                <button
                  className="consulta-btn-secondary"
                  onClick={() => setPedidoDesdobramento(pedido.idPedido)}
                >
                  🔗 Ver vínculo TV7/TV8
                </button>
              </div>
            )}

            {/* Nota Fiscal */}
            {pedido.numeroNotaFiscal && (
              <div className="resultado-nf">
                <span className="nf-icon">🧾</span>
                <span className="nf-label">Nota Fiscal:</span>
                <span className="nf-numero">NF {pedido.numeroNotaFiscal}</span>
              </div>
            )}

            {/* Grid de informações */}
            <div className="resultado-grid">
              <div className="resultado-card">
                <h3>📦 Dados do Pedido</h3>
                <table className="info-table">
                  <tbody>
                    <tr><td>Número</td><td>#{pedido.idPedido}</td></tr>
                    <tr><td>Status</td><td style={{ color: status.cor, fontWeight: 700 }}>{status.texto}</td></tr>
                    <tr><td>Data</td><td>{formatarData(pedido.createData)}</td></tr>
                    <tr><td>Valor Total</td><td>{formatarMoeda(pedido.valorTotal)}</td></tr>
                    <tr><td>Qtd. Itens</td><td>{pedido.totalItens || 0}</td></tr>
                    <tr><td>Plano de Pagamento</td><td>{getPaymentPlan(pedido.paymentPlanId)}</td></tr>
                    <tr><td>Tipo de Venda</td><td>{SALE_TYPE_MAP[pedido.saleType] || pedido.saleType || '—'}</td></tr>
                    <tr><td>Origem</td><td>{SALE_ORIGIN_MAP[pedido.saleOrigin] || pedido.saleOrigin || '—'}</td></tr>
                    <tr><td>RCA</td><td>{pedido.sellerId || '—'}</td></tr>
                    {pedido.numeroNotaFiscal && <tr><td>Nota Fiscal</td><td>{pedido.numeroNotaFiscal}</td></tr>}
                    {pedido.observation && <tr><td>Observação</td><td>{pedido.observation}</td></tr>}
                  </tbody>
                </table>
              </div>

              {pedido.cliente && (
                <div className="resultado-card">
                  <h3>🏢 Cliente</h3>
                  <table className="info-table">
                    <tbody>
                      <tr><td>Nome</td><td>{pedido.cliente.nome}</td></tr>
                      {pedido.cliente.tradeName && pedido.cliente.tradeName !== 'N/A' && (
                        <tr><td>Fantasia</td><td>{pedido.cliente.tradeName}</td></tr>
                      )}
                      <tr><td>CNPJ/CPF</td><td>{formatarDoc(pedido.cliente.cnpj)}</td></tr>
                      {pedido.cliente.email && pedido.cliente.email !== 'N/A' && (
                        <tr><td>E-mail</td><td>{pedido.cliente.email}</td></tr>
                      )}
                      {pedido.cliente.phone && pedido.cliente.phone !== 'N/A' && (
                        <tr><td>Telefone</td><td>{pedido.cliente.phone}</td></tr>
                      )}
                      {pedido.cliente.businessCity && pedido.cliente.businessCity !== 'N/A' && (
                        <tr><td>Cidade</td><td>{pedido.cliente.businessCity}</td></tr>
                      )}
                      {pedido.cliente.deliveryAddress && pedido.cliente.deliveryAddress !== 'N/A' && (
                        <tr>
                          <td>Endereço</td>
                          <td>
                            {pedido.cliente.deliveryAddress}
                            {pedido.cliente.deliveryAddressNumber ? ', ' + pedido.cliente.deliveryAddressNumber : ''}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Itens do Pedido */}
            {pedido.itens && pedido.itens.length > 0 && (
              <div className="resultado-itens">
                <h3>🛒 Itens do Pedido</h3>
                <div className="itens-table-wrapper">
                  <table className="itens-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Produto</th>
                        <th>SKU</th>
                        <th>Qtd</th>
                        <th>Preço Unit.</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pedido.itens.map((item, i) => (
                        <tr key={i}>
                          <td>{item.position || i + 1}</td>
                          <td className="produto-nome">
                            {item.productName || '—'}
                            {item.productId && (
                              <small className="produto-id"> (#{item.productId})</small>
                            )}
                          </td>
                          <td>{item.productSKUERPReferenceKey || '—'}</td>
                          <td>{item.quantity ?? '—'}</td>
                          <td>{formatarMoeda(item.sellPrice)}</td>
                          <td>{formatarMoeda((item.sellPrice || 0) * (item.quantity || 1))}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="5" style={{ textAlign: 'right', fontWeight: 700 }}>Total do Pedido</td>
                        <td style={{ fontWeight: 700, color: '#10b981' }}>
                          {formatarMoeda(pedido.valorTotal)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

          </div>
        )}
      </div>

      {pedidoDesdobramento && (
        <Desdobramento
          orderId={pedidoDesdobramento}
          onClose={() => setPedidoDesdobramento(null)}
        />
      )}
    </div>
  );
}
